import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import ShowcaseLayout from "../components/ShowcaseLayout";
import type { Layout, ResponsiveLayouts } from "react-grid-layout";
import { TradingViewWidget } from "../components/tradingview";
import Orderbook from "../components/Orderbook";
import TradePrice from "../components/TradePrice";
import TradeOperation from "../components/TradeOperation";
import PendingOrders from "../components/PendingOrders";
import { useMarkets } from "../hooks/useMarkets";
import { useOnboard } from "../hooks/useOnboard";
import { DEFAULT_BASE_URL, DEFAULT_WS_URL } from "../configs/base";
import BucketPositions from "../components/BucketPositions";
import BucketCollections from "../components/BucketCollections";
import { LeverageModal } from "../components/smartlayout/modals";

interface TestLayoutExchangeProps {}

interface LayoutItem extends Layout {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

function TestLayoutExchange(_props: TestLayoutExchangeProps) {
  const [layout, setLayout] = useState<LayoutItem[]>([]);
  const [compactType, setCompactType] = useState<
    "vertical" | "horizontal" | null
  >("vertical");
  const [allStatic, setAllStatic] = useState(true);
  const [fullWidth, setFullWidth] = useState(false);
  const [selectedBucketId, setSelectedBucketId] = useState<string | null>(null);
  const [selectedBucketBalance, setSelectedBucketBalance] = useState<
    number | undefined
  >(undefined);
  const [isLeverageModalOpen, setIsLeverageModalOpen] = useState(false);
  const [tempLeverage, setTempLeverage] = useState(10);
  const showcaseLayoutRef = useRef<ShowcaseLayout>(null);
  const tradeOperationRef =
    useRef<import("../components/TradeOperation").TradeOperationRef>(null);

  // Ref to track if layout has been changed (allows only one change)
  const hasChangedLayoutRef = useRef(false);

  // Refs for stable values that don't need to trigger rerenders
  const baseURLRef = useRef(DEFAULT_BASE_URL);
  const wsURLRef = useRef(DEFAULT_WS_URL);

  // Store previous layout for efficient comparison (avoid JSON.stringify)
  const prevLayoutRef = useRef<LayoutItem[]>([]);

  // Configuration - use refs to avoid unnecessary rerenders
  const baseURL = baseURLRef.current;
  const wsURL = wsURLRef.current;

  // Load authToken from localStorage (shared with other test pages)
  const [sessionToken, setSessionToken] = useState(() => {
    return localStorage.getItem("authToken") || "";
  });

  // Use markets hook to manage market data retention
  // With autoFetchMarkets: true, markets are automatically fetched on mount
  // The store (useMarkets.ts) automatically selects the first active market (or first market)
  // when markets are loaded, so selectedMarketId will be set automatically
  const {
    markets,
    selectedMarketId,
    selectedMarketSymbol,
    setSelectedMarketId,
    loadingMarkets,
    getError,
  } = useMarkets({
    baseURL,
    sessionToken,
    autoFetchMarkets: true, // Auto-fetch markets on mount and auto-select first market
  });

  // Use onboarding hook as single source of truth for account information
  const {
    mnemonic,
    walletAddress,
    authToken: onboardAuthToken,
    setMnemonic: setOnboardMnemonic,
  } = useOnboard(baseURL);

  // Initialize mnemonic with default value if not set
  useEffect(() => {
    if (!mnemonic) {
      setOnboardMnemonic(
        "legal winner thank year wave sausage worth useful legal winner thank yellow",
      );
    }
  }, [mnemonic, setOnboardMnemonic]);

  // Update prevLayoutRef when layout changes from external sources
  useEffect(() => {
    prevLayoutRef.current = layout;
  }, [layout]);

  // Sync onboard auth token to sessionToken when it changes
  useEffect(() => {
    if (onboardAuthToken) {
      setSessionToken(onboardAuthToken);
      localStorage.setItem("authToken", onboardAuthToken);
    }
  }, [onboardAuthToken]);

  // Sync sessionToken to localStorage whenever it changes (except when syncing from localStorage)
  // Use ref to track if we're syncing from localStorage to prevent loops
  const isSyncingFromStorageRef = useRef(false);
  useEffect(() => {
    if (
      sessionToken &&
      sessionToken.trim() !== "" &&
      !isSyncingFromStorageRef.current
    ) {
      const stored = localStorage.getItem("authToken");
      if (stored !== sessionToken) {
        localStorage.setItem("authToken", sessionToken);
      }
    }
    isSyncingFromStorageRef.current = false;
  }, [sessionToken]);

  // Listen for authToken changes from localStorage (when other pages/tabs update it)
  // Optimized: increased interval and use ref to prevent unnecessary updates
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "authToken" && e.newValue) {
        isSyncingFromStorageRef.current = true;
        setSessionToken(e.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Check localStorage periodically - increased interval to reduce checks (5s instead of 1s)
    const interval = setInterval(() => {
      const storedAuthToken = localStorage.getItem("authToken");
      // Use functional update to avoid dependency on sessionToken
      setSessionToken((currentToken) => {
        if (storedAuthToken && storedAuthToken !== currentToken) {
          isSyncingFromStorageRef.current = true;
          return storedAuthToken;
        }
        return currentToken;
      });
    }, 5000); // Reduced frequency: 5 seconds instead of 1

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []); // Empty deps - only run once on mount

  // Market ID selection flow:
  // 1. Component mounts with autoFetchMarkets: true
  // 2. useMarkets hook automatically calls fetchMarkets() when markets array is empty
  // 3. fetchMarkets() loads markets from API and calls setMarkets() in the store
  // 4. setMarkets() in marketStore.ts automatically selects the first active market
  //    (or first market) if selectedMarketId is null
  // 5. Component re-renders with selectedMarketId set, ready to use
  // No additional useEffect needed - the store handles auto-selection automatically

  // Compute default market ID (first active market, or first in list)
  // Use market_id (string) for the store (selectedMarketId is string)
  // Memoize to prevent recalculation on every render
  const defaultMarketId = useMemo(() => {
    return markets.length > 0
      ? (markets.find((m) => m.active)?.market_id ??
          markets[0].market_id ??
          markets.find((m) => m.active)?.id?.toString() ??
          markets[0].id?.toString() ??
          null)
      : null;
  }, [markets]);

  // Optimized layout change handler with efficient comparison and one-time change limit
  // Only processes changes when not in static mode and hasn't changed yet
  const onLayoutChange = useCallback(
    (newLayout: Layout[], _layouts: ResponsiveLayouts) => {
      // If in static mode, ignore layout changes to prevent unnecessary updates
      if (allStatic) {
        return;
      }

      // Efficient comparison without JSON.stringify - compare item by item
      const prevLayout = prevLayoutRef.current;
      const newLayoutItems = newLayout as LayoutItem[];

      // Quick length check
      if (prevLayout.length !== newLayoutItems.length) {
        hasChangedLayoutRef.current = true;
        prevLayoutRef.current = newLayoutItems;
        setLayout(newLayoutItems);
        return;
      }

      // Efficient item-by-item comparison (much faster than JSON.stringify)
      let hasChanged = false;
      for (let i = 0; i < prevLayout.length; i++) {
        const prev = prevLayout[i];
        const next = newLayoutItems[i];
        if (
          prev.x !== next.x ||
          prev.y !== next.y ||
          prev.w !== next.w ||
          prev.h !== next.h ||
          prev.i !== next.i
        ) {
          hasChanged = true;
          break;
        }
      }

      // Only update if layout actually changed
      if (hasChanged) {
        hasChangedLayoutRef.current = true;
        prevLayoutRef.current = newLayoutItems;
        setLayout(newLayoutItems);
      }
    },
    [allStatic],
  ); // Only recreate when allStatic changes

  const handleCompactTypeChange = useCallback(() => {
    setCompactType((prev) =>
      prev === "horizontal"
        ? "vertical"
        : prev === "vertical"
          ? null
          : "horizontal",
    );
  }, []);

  const handleToggleFullWidth = useCallback(() => {
    setFullWidth((prev) => !prev);
  }, []);

  // Apply fullWidth class to body when fullWidth state changes
  useEffect(() => {
    if (fullWidth) {
      document.body.classList.add("full-width-screen");
    } else {
      document.body.classList.remove("full-width-screen");
    }
    // Cleanup on unmount
    return () => {
      document.body.classList.remove("full-width-screen");
    };
  }, [fullWidth]);

  const handleToggleStatic = useCallback(() => {
    setAllStatic((prev) => !prev);
    // Reset layout change flag when toggling static mode
    // This allows one more layout change if user unlocks
    if (allStatic) {
      hasChangedLayoutRef.current = false;
    }
  }, [allStatic]);

  const handleNewLayout = useCallback(() => {
    if (showcaseLayoutRef.current) {
      // Reset the layout change flag when generating new layout
      hasChangedLayoutRef.current = false;
      showcaseLayoutRef.current.generateNewLayout();
    }
  }, []);

  // Memoize stringifyLayout to prevent recalculation on every render
  const stringifyLayout = useMemo(() => {
    return layout.map(function (l) {
      return (
        <div className="layoutItem" key={l.i}>
          <b>{l.i}</b>: [{l.x}, {l.y}, {l.w}, {l.h}]
        </div>
      );
    });
  }, [layout]);

  // Ensure selectedMarketId is a string (type guard) - compute before early returns
  // This allows hooks below to use it safely even if markets aren't loaded yet
  const currentMarketId: string = selectedMarketId || "";

  // Calculate dimensions for each slot based on layout
  // Using useMemo for derived state to prevent unnecessary recalculations and loops
  // This is recalculated only when layout changes, avoiding state update cycles
  // MUST be called before any early returns to follow Rules of Hooks
  // Grid configuration constants (stable, no need to memoize)
  const ROW_HEIGHT = 30;
  const MARGIN_Y = 10;
  const COLS = 12;

  const slotDimensions = useMemo(() => {
    if (layout.length === 0) {
      return {};
    }

    const dimensions: {
      [slotIndex: number]: { width: string; height: string };
    } = {};

    // Calculate dimensions for slots 0-6 (7 slots total)
    for (let slotIndex = 0; slotIndex <= 6; slotIndex++) {
      const layoutItem = layout.find((item) => item.i === slotIndex.toString());

      if (layoutItem) {
        // Calculate pixel height: h * rowHeight + (h - 1) * verticalMargin
        const pixelHeight =
          layoutItem.h * ROW_HEIGHT + (layoutItem.h - 1) * MARGIN_Y;

        // Calculate width as percentage for responsiveness
        const widthPercent = (layoutItem.w / COLS) * 100;

        dimensions[slotIndex] = {
          width: `${widthPercent}%`,
          height: `${pixelHeight}px`,
        };
      }
    }

    return dimensions;
  }, [layout]);

  // Demo components 1-5 for slots 1-5
  // Use currentMarketId in the key to force re-render when market changes
  // Memoize to prevent unnecessary re-creation of components
  // Dimensions are dynamically calculated from layout and passed to components that support them
  // MUST be called before any early returns to follow Rules of Hooks
  // Optimized: Split into separate memoized components to reduce dependencies
  const demoComponents: React.ReactNode[] = useMemo(
    () => [
      // Slot 1: TradingViewWidget - stable, no dependencies
      <TradingViewWidget
        key="tv"
        symbol="BINANCE:BTCUSDT"
        interval="5"
        isCollapsed={false}
      />,
      // Slot 2: Orderbook - uses dynamically calculated height
      <Orderbook
        key={`clob-${currentMarketId}`}
        baseURL={baseURL}
        wsURL={wsURL}
        marketId={currentMarketId}
        maxDepth={20}
        height={slotDimensions[1]?.height}
        authToken={sessionToken}
      />,
      // Slot 3: TradePrice - pass symbol to enable mark_price subscription
      <TradePrice
        key={`price-${currentMarketId}`}
        baseURL={baseURL}
        wsURL={wsURL}
        marketId={currentMarketId}
        symbol={selectedMarketSymbol || undefined} // Enable mark_price subscription
        authToken={sessionToken}
      />,
      // Slot 4: TradeOperation
      <TradeOperation
        ref={tradeOperationRef}
        key={`trade-${currentMarketId}`}
        baseURL={baseURL}
        marketId={currentMarketId}
        selectedBucketBalance={selectedBucketBalance}
        selectedBucketId={selectedBucketId}
        onLeverageClick={() => {
          if (tradeOperationRef.current) {
            setTempLeverage(tradeOperationRef.current.leverage);
          }
          setIsLeverageModalOpen(true);
        }}
        // marketSymbol is now auto-derived from selected market via useMarkets
      />,
      // Slot 5: PendingOrders - uses dynamically calculated height
      <PendingOrders
        key={`order-${currentMarketId}`}
        baseURL={baseURL}
        wsURL={wsURL}
        authToken={sessionToken}
        walletAddress={walletAddress}
        mnemonic={mnemonic}
        height={slotDimensions[4]?.height}
        refreshInterval={5000}
      />,
      <BucketPositions
        key={`pos-${currentMarketId}`}
        baseURL={baseURL}
        authToken={sessionToken}
        walletAddress={walletAddress}
        height={slotDimensions[5]?.height}
        refreshInterval={5000}
        selectedBucketId={selectedBucketId}
      />,
      <BucketCollections
        key={`bucket-${currentMarketId}`}
        baseURL={baseURL}
        mnemonic={mnemonic}
        authToken={sessionToken}
        walletAddress={walletAddress}
        height={slotDimensions[6]?.height}
        refreshInterval={5000}
        onBucketSelect={(bucketId, nominalValue) => {
          setSelectedBucketId(bucketId);
          setSelectedBucketBalance(nominalValue);
        }}
      />,
    ],
    [
      baseURL,
      wsURL,
      sessionToken,
      walletAddress,
      currentMarketId,
      selectedMarketSymbol, // Include symbol in dependencies
      slotDimensions,
      selectedBucketId,
      selectedBucketBalance,
    ],
  );

  // Show loading state while markets are being fetched
  // Early returns MUST come AFTER all hooks to follow Rules of Hooks
  if (loadingMarkets) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <div>Loading markets...</div>
      </div>
    );
  }

  // Show error state if markets failed to load
  const marketsError = getError("markets");
  if (marketsError) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "#ff6b6b" }}>
        <div>Error loading markets: {marketsError}</div>
      </div>
    );
  }

  // Show message if no markets available or no market selected
  if (markets.length === 0 || !selectedMarketId) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <div>No markets available.</div>
      </div>
    );
  }

  return (
    <div>
      {/* Configuration Section */}
      <div className="App-section">
        <h3 style={{ marginTop: 0, marginBottom: "0.75rem" }}>Configuration</h3>
        <div
          style={{
            display: "flex",
            gap: "1rem",
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: "1", minWidth: "300px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.25rem",
                fontWeight: "500",
              }}
            >
              Market:
            </label>
            <select
              value={selectedMarketId || ""}
              onChange={(e) => {
                const val = e.target.value;
                if (val) {
                  // Use string market_id directly for the store
                  setSelectedMarketId(val);
                }
              }}
              style={{
                width: "100%",
                padding: "0.5rem",
                fontSize: "1rem",
                borderRadius: "4px",
                border: "1px solid #ccc",
                backgroundColor: "white",
                color: "#333",
              }}
            >
              {markets.length === 0 ? (
                <option value="">Loading markets...</option>
              ) : (
                <>
                  <option value="" disabled>
                    -- Select a market --
                  </option>
                  {markets.map((market) => {
                    // Use market_id (string) for the store (selectedMarketId is string)
                    // Fallback to id converted to string if market_id doesn't exist
                    const marketIdValue =
                      market.market_id ?? market.id?.toString() ?? "";

                    // For display, use market_id if available (might be more descriptive), otherwise use id
                    const displayId =
                      market.market_id ?? market.id?.toString() ?? "";
                    const idDisplay =
                      displayId.length > 8
                        ? `${displayId.substring(0, 8)}...`
                        : displayId;

                    // Prioritize moniker (from API), then symbol, then fallback
                    const displayName = market.moniker
                      ? market.moniker
                      : market.symbol
                        ? market.symbol
                        : market.baseAsset && market.quoteAsset
                          ? `${market.baseAsset}-${market.quoteAsset}`
                          : `Market ID: ${displayId}`;
                    const isActive = market.active ? " [ACTIVE]" : "";

                    return (
                      <option key={marketIdValue} value={marketIdValue}>
                        {displayName} - ID: {idDisplay}
                        {isActive}
                      </option>
                    );
                  })}
                </>
              )}
            </select>
            {markets.length > 0 && (
              <small
                style={{
                  display: "block",
                  marginTop: "0.25rem",
                  color:
                    selectedMarketId === defaultMarketId
                      ? "#51cf66"
                      : "#ff9f43",
                }}
              >
                {selectedMarketId === defaultMarketId
                  ? `✓ Auto-selected default market (ID: ${defaultMarketId?.substring(0, 16)}...)`
                  : `⚠ Manual selection (ID: ${selectedMarketId?.substring(0, 16)}...)`}
              </small>
            )}
          </div>
          <div style={{ flex: "1", minWidth: "300px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.25rem",
                fontWeight: "500",
              }}
            >
              Mnemonic (for wallet address):
            </label>
            <input
              type="text"
              value={mnemonic || ""}
              onChange={(e) => setOnboardMnemonic(e.target.value)}
              placeholder="Enter mnemonic to generate wallet address"
              style={{
                width: "100%",
                padding: "0.5rem",
                fontSize: "1rem",
                border: "1px solid #ccc",
                backgroundColor: "white",
                color: "#333",
              }}
            />
          </div>
          <div style={{ flex: "1", minWidth: "300px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.25rem",
                fontWeight: "500",
              }}
            >
              Wallet Address:
            </label>
            <input
              type="text"
              value={walletAddress || ""}
              disabled={!!(mnemonic && mnemonic.trim() !== "")}
              placeholder="automatic (from mnemonic via useOnboard)"
              style={{
                width: "100%",
                padding: "0.5rem",
                fontSize: "1rem",
                border: "1px solid #ccc",
                backgroundColor: walletAddress ? "#e7f3ff" : "#f5f5f5",
                color: "#333",
              }}
            />
            {walletAddress && (
              <small
                style={{
                  display: "block",
                  marginTop: "0.25rem",
                  color: "#28a745",
                }}
              >
                ✓ Auto-generated from mnemonic via useOnboard hook
              </small>
            )}
          </div>
        </div>
        <h3>Layout Controls</h3>
        <button onClick={handleNewLayout} style={{ marginRight: "0.5rem" }}>
          Generate New Layout
        </button>
        <button onClick={handleToggleStatic} style={{ marginRight: "0.5rem" }}>
          {allStatic ? "Enable Dragging" : "Disable Dragging"}
        </button>
        <button
          onClick={handleCompactTypeChange}
          style={{ marginRight: "0.5rem" }}
        >
          Change Compaction Type
        </button>
        <button onClick={handleToggleFullWidth}>Full Width Screen</button>
        <div style={{ marginTop: "0.5rem", fontSize: "0.9em", color: "#fff" }}>
          Compaction type:{" "}
          {compactType
            ? compactType.charAt(0).toUpperCase() + compactType.slice(1)
            : "No Compaction"}
        </div>
      </div>

      <ShowcaseLayout
        ref={showcaseLayoutRef}
        onLayoutChange={onLayoutChange}
        slotContent={demoComponents}
        compactType={compactType}
        allStatic={allStatic}
        onCompactTypeChange={handleCompactTypeChange}
        onToggleStatic={handleToggleStatic}
        onNewLayout={handleNewLayout}
      />
      <div className="layoutJSON">
        Displayed as <code>[x, y, w, h]</code>:
        <div className="columns">{stringifyLayout}</div>
      </div>
      {/* Leverage Selection Modal */}
      <LeverageModal
        isOpen={isLeverageModalOpen}
        currentLeverage={tradeOperationRef.current?.leverage || 10}
        tempLeverage={tempLeverage}
        maxRatio={
          tradeOperationRef.current
            ? tradeOperationRef.current.calculateMaxRatio(tempLeverage)
            : tempLeverage.toFixed(2)
        }
        onLeverageChange={setTempLeverage}
        onConfirm={() => {
          if (tradeOperationRef.current) {
            tradeOperationRef.current.setLeverage(tempLeverage);
          }
          setIsLeverageModalOpen(false);
        }}
        onCancel={() => {
          if (tradeOperationRef.current) {
            setTempLeverage(tradeOperationRef.current.leverage);
          }
          setIsLeverageModalOpen(false);
        }}
      />
    </div>
  );
}

export default TestLayoutExchange;
